import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { useAuction } from '../context/AuctionContext';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';

const POSITION_MAP = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Coach'
};

function BlindPoolRound({ round, onAssignPlayers, onRecalculate, onDeleteRound, onDeleteBid, transfers = [] }) {
    const { auctionId } = useAuction();
    const [bids, setBids] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auctionId || !round.id) return;

        const bidsQuery = query(
            collection(db, 'auctions', auctionId, 'blindPoolRounds', round.id, 'bids')
        );

        const unsubscribe = onSnapshot(bidsQuery, (snapshot) => {
            let bidsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Sort bids on the client
            bidsData.sort((a, b) => {
                if (!a.playerName || !b.playerName) return 0; // Guard against missing data
                
                const nameComparison = a.playerName.localeCompare(b.playerName);
                if (nameComparison !== 0) return nameComparison;

                const amountComparison = b.amount - a.amount;
                if (amountComparison !== 0) return amountComparison;

                const dateA = a.timestamp?.toDate() || new Date(0);
                const dateB = b.timestamp?.toDate() || new Date(0);
                return dateA - dateB;
            });

            setBids(bidsData);
            setLoading(false);
        }, (err) => {
            console.error(`Error fetching bids for round ${round.id}:`, err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [auctionId, round.id]);

    const handleAssignPlayersClick = () => {
        if (onAssignPlayers) {
            onAssignPlayers(round.id, bids);
        }
    };

    const handleRecalculateClick = () => {
        if (onRecalculate) {
            onRecalculate(round.id);
        }
    };

    const winningBids = React.useMemo(() => {
        const winningSet = new Set();
        transfers
            .filter(t => t.type === 'blindPoolAssignment' && t.roundId === round.id)
            .forEach(t => {
                winningSet.add(`${t.playerId}-${t.to}`);
            });
        return winningSet;
    }, [transfers, round.id]);


    return (
        <Box sx={{ mb: 4 }} component={Paper} p={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    Ronde {round.roundNumber} <Typography variant='caption'>({round.status})</Typography>
                </Typography>
                <Box>
                    {round.status === 'open' && (
                        <Button variant="contained" onClick={handleAssignPlayersClick} sx={{ mr: 1 }}>
                            Spelers Toekennen
                        </Button>
                    )}
                    {round.status === 'closed' && (
                        <Button variant="outlined" onClick={handleRecalculateClick} sx={{ mr: 1 }}>
                            Herbepalen
                        </Button>
                    )}
                    <Button variant="outlined" color="error" onClick={() => onDeleteRound(round.id)}>
                        Verwijderen
                    </Button>
                </Box>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Speler</TableCell>
                            <TableCell>Club</TableCell>
                            <TableCell>Positie</TableCell>
                            <TableCell>Deelnemer</TableCell>
                            <TableCell align="right">Bedrag</TableCell>
                            <TableCell>Datum/tijd</TableCell>
                            <TableCell align="center">Toegekend</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell>
                            </TableRow>
                        ) : bids.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">Nog geen biedingen geplaatst in deze ronde.</TableCell>
                            </TableRow>
                        ) : (
                            bids.map(bid => {
                                const isWinningBid = winningBids.has(`${bid.playerId}-${bid.participantId}`);
                                return (
                                    <TableRow key={bid.id} hover>
                                        <TableCell>{bid.playerName}</TableCell>
                                        <TableCell>{bid.playerClub}</TableCell>
                                        <TableCell>{POSITION_MAP[bid.playerPosition] || bid.playerPosition}</TableCell>
                                        <TableCell>{bid.participantName}</TableCell>
                                        <TableCell align="right">{new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(bid.amount)}</TableCell>
                                        <TableCell>{bid.timestamp?.toDate().toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</TableCell>
                                        <TableCell align="center">
                                            {isWinningBid ? <CheckCircleIcon color="success" /> : ''}
                                        </TableCell>
                                        <TableCell>
                                            {round.status === 'closed' && isWinningBid && (
                                                <IconButton size="small" onClick={() => onDeleteBid(bid, round.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default BlindPoolRound;